import { data, useActionData } from 'react-router'
import { BookDemoTabbedForm } from '#app/components/book-demo-tabbed-form.tsx'
import {
	FeatureGrid,
	MarketingButton,
	MarketingHero,
	MarketingPageShell,
	MarketingSection,
	type MarketingFeature,
} from '#app/components/marketing-page-shell.tsx'
import {
	createFaqSchema,
	motorcycleDealerWebsiteSchema,
} from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { type Route } from './+types/motorcycle-dealers.ts'

const MetaTitle = `Motorcycle Dealer Websites | Fast, Mobile-First Sites Built to Sell`
const MetaDescription = `Mobile-first websites built for motorcycle dealerships. Model and spec browsing, financing pre-qualification, and local SEO built to turn riders into buyers.`

const directScheduleNamespace = 'website-demo-direct'

export const handle = { chrome: 'payload' }

const features: MarketingFeature[] = [
	{
		label: '01',
		title: 'Mobile-first model and spec browsing',
		description:
			'Riders compare models, trims, and specs on their phone long before visiting a dealership. Fast, mobile-first pages make that comparison effortless instead of frustrating.',
	},
	{
		label: '02',
		title: 'Speed that protects conversions',
		description:
			'A slow page during that comparison sends a rider to the next dealership in their search results. The platform is built around Core Web Vitals so pages stay fast as inventory grows.',
	},
	{
		label: '03',
		title: 'Financing and pre-qualification built in',
		description:
			'Riders can start a financing conversation before they ever call, moving qualified buyers into your showroom instead of losing them to a slower checkout process.',
	},
	{
		label: '04',
		title: 'Local SEO for dealership-locator searches',
		description:
			'Location pages, schema markup, and inventory-aware content help your dealership show up when riders search for a model near them.',
	},
	{
		label: '05',
		title: 'A no-code content editor',
		description:
			'Update inventory, offers, events, and blog posts without waiting on a developer, so your site stays current through every riding season.',
	},
	{
		label: '06',
		title: 'Inventory sync across every location',
		description:
			'Multi-location inventory sync keeps listings accurate the moment a bike arrives, sells, or changes price, across every rooftop in your group.',
	},
]

const faqs = [
	{
		question: 'Do you build websites specifically for motorcycle dealers?',
		answer:
			'Yes. DealerProEdge websites for motorcycle dealers are built around how riders research and buy, with model and spec browsing, financing pre-qualification, and local SEO built for the category.',
	},
	{
		question: 'How does the platform help riders find my dealership?',
		answer:
			'Local SEO fundamentals like schema markup, location pages, and inventory-aware content help your dealership show up when riders search for a specific model or dealership near them.',
	},
	{
		question: 'Can riders start financing before visiting the showroom?',
		answer:
			'Yes. Financing and pre-qualification tools let riders start that conversation online, so your team receives a qualified lead instead of a cold walk-in.',
	},
	{
		question: 'Will my team be able to update the site without a developer?',
		answer:
			'Yes. The no-code content editor lets your team update inventory, offers, events, and blog posts directly, so the site stays current through every riding season.',
	},
]

const faqSchema = createFaqSchema(faqs)

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/industries/motorcycle-dealers'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': motorcycleDealerWebsiteSchema },
		{ 'script:ld+json': faqSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(
		formData,
		'website-demo',
		args,
	)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function MotorcycleDealersPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Motorcycle Dealers"
				title="Websites built to turn riders into buyers."
				subtitle="Riders compare models, trims, and financing on their phone long before they ever walk into a showroom. DealerProEdge gives motorcycle dealers a fast, mobile-first website with the inventory tools and local SEO built for how riders actually shop."
				stats={[
					{ value: '70%+', label: 'Riders research on mobile first' },
					{ value: '20%', label: 'Conversions lost per second of delay' },
					{ value: '24/7', label: 'Inventory visibility' },
				]}
				actions={
					<>
						<MarketingButton to="#getStarted">Schedule My Demo</MarketingButton>
						<MarketingButton to="/car-dealer-websites" variant="light">
							See the Platform
						</MarketingButton>
					</>
				}
			/>

			<MarketingSection
				eyebrow="The reality"
				title="Riders are comparing models before they ever call."
			>
				<div className="text-black/66 max-w-4xl text-xl leading-9">
					<p>
						Today&apos;s motorcycle buyers browse models, compare specs, and
						start financing conversations from their phone, often before a
						salesperson ever hears from them. A slow or dated website sends that
						rider to the next dealership in their search results instead of your
						showroom.
					</p>
				</div>
			</MarketingSection>

			<MarketingSection
				eyebrow="Built for riders"
				title="Everything your dealership needs to move faster."
			>
				<FeatureGrid features={features} />
			</MarketingSection>

			<MarketingSection
				eyebrow="Frequently asked questions"
				title="What dealers ask before switching."
			>
				<div className="grid grid-cols-1 border-l border-t border-black lg:grid-cols-2">
					{faqs.map((faq) => (
						<div
							className="border-b border-r border-black bg-[#fffffa]/60 p-7"
							key={faq.question}
						>
							<h3 className="text-xl font-medium leading-snug">
								{faq.question}
							</h3>
							<p className="text-black/64 mt-4 text-base leading-7">
								{faq.answer}
							</p>
						</div>
					))}
				</div>
			</MarketingSection>

			<MarketingSection id="getStarted" className="scroll-mt-24">
				<div className="mx-auto max-w-[620px]">
					<div className="mb-8 text-center">
						<h2 className="text-4xl font-medium leading-tight text-black md:text-5xl">
							Schedule a Demo
						</h2>
						<p className="text-black/58 mx-auto mt-4 max-w-[520px] text-base leading-7 md:text-lg">
							Submit the form below or schedule a time directly on our calendar.
						</p>
					</div>
					<BookDemoTabbedForm
						isSubmitted={Boolean(didSubmit)}
						lastResult={lastResult}
						scheduleNamespace={directScheduleNamespace}
						successDescription="Your website demo request went to the DealerProEdge team. We will review your current site and follow up shortly."
						usePairedFieldRows
					/>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	)
}
